import axios from "axios";
import API_BASE_URL from "../../../config/apiConfig";

const API_URL = `${API_BASE_URL}/api/Mfa`;

// ============================================================
// Servicio de Autenticación de Dos Factores (2FA / TOTP)
// Controller: MfaController
// Ruta base: /api/Mfa
// Estándar: TOTP RFC 6238 (compatible con Microsoft Authenticator,
//           Google Authenticator, Authy, 1Password, Bitwarden, etc.)
//
// Filosofía: el backend hace TODA la criptografía (generar secreto,
// encriptar AES-256, validar TOTP con Otp.NET, hashear recovery codes
// con HMACSHA256). El frontend solo envía/recibe texto plano.
//
// Tokens:
//  - JWT completo (8h): para start-enrollment, confirm-enrollment,
//    disable, frequency.
//  - Pre-auth token (2min, claim scope=mfa_only): para verify y recovery.
//    Se emite desde /api/AuthV2/login cuando mfaRequired=true.
// ============================================================

/**
 * Iniciar enrollment de 2FA. El backend genera un secreto TOTP aleatorio
 * (20 bytes → Base32), lo encripta con AES-256 y lo guarda en BD. Devuelve
 * la URI otpauth:// estándar para que el frontend pinte el QR.
 *
 * El 2FA NO queda activo tras este llamado — queda pendiente hasta que el
 * usuario confirme con `confirmEnrollment` ingresando el primer código TOTP.
 *
 * Autenticación: JWT completo. El employeeId y email se leen del token.
 *
 * Ejemplo de llamada:
 * POST /api/Mfa/start-enrollment
 * (sin body)
 *
 * Respuesta exitosa (200):
 * {
 *   "success": true,
 *   "message": "Scan the QR code with Microsoft Authenticator",
 *   "otpAuthUri": "otpauth://totp/MCMVisual:user%40example.com?secret=JBSWY3DPEHPK3PXP&issuer=MCMVisual&algorithm=SHA1&digits=6&period=30"
 * }
 *
 * Posibles errores (400):
 * { "success": false, "result": 1, "message": "User not found or inactive" }
 * { "success": false, "result": 2, "message": "MFA already enrolled and active. Disable it first" }
 */
export const startMfaEnrollment = async () => {
  try {
    const response = await axios.post(
      `${API_URL}/start-enrollment`,
      {},
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      return error.response.data;
    }
    console.error("Error en startMfaEnrollment:", error);
    throw error;
  }
};

/**
 * Confirmar el enrollment de 2FA validando el primer código TOTP que el
 * usuario ve en su app authenticator. Si el código es válido, el backend:
 *  - Activa BIT_MFA_ENABLED = 1
 *  - Genera 8 códigos de recuperación aleatorios (GUIDs formato xxxx-xxxx-xxxx)
 *  - Hashea cada código con HMACSHA256 y los guarda en TAB_MFA_RECOVERY_CODES
 *  - Retorna los 8 códigos EN TEXTO PLANO una sola vez
 *
 * IMPORTANTE: los recoveryCodes devueltos solo se muestran UNA VEZ.
 * El frontend debe forzar al usuario a guardarlos (modal no cerrable con
 * botones Descargar/Copiar/Imprimir) y nunca persistirlos en localStorage.
 *
 * Autenticación: JWT completo.
 *
 * @param {string} totpCode - Código TOTP de 6 dígitos que muestra la app authenticator
 *
 * Ejemplo de llamada:
 * POST /api/Mfa/confirm-enrollment
 * { "totpCode": "123456" }
 *
 * Respuesta exitosa (200):
 * {
 *   "success": true,
 *   "message": "2FA activated successfully. Save your recovery codes in a safe place.",
 *   "recoveryCodes": [
 *     "a1b2-c3d4-e5f6", "g7h8-i9j0-k1l2", "m3n4-o5p6-q7r8", "s9t0-u1v2-w3x4",
 *     "y5z6-a7b8-c9d0", "e1f2-g3h4-i5j6", "k7l8-m9n0-o1p2", "q3r4-s5t6-u7v8"
 *   ]
 * }
 *
 * Posibles errores (400):
 * { "success": false, "result": 1, "message": "User not found or inactive" }
 * { "success": false, "result": 2, "message": "MFA secret not found. Start enrollment first." }
 * { "success": false, "result": 3, "message": "MFA already active" }
 * { "success": false, "result": 4, "message": "Invalid TOTP code. Please try again." }
 */
export const confirmMfaEnrollment = async (totpCode) => {
  try {
    const response = await axios.post(
      `${API_URL}/confirm-enrollment`,
      { totpCode },
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      return error.response.data;
    }
    console.error("Error en confirmMfaEnrollment:", error);
    throw error;
  }
};

/**
 * Verificar código TOTP durante el flujo de login. Se llama después de que
 * /api/AuthV2/login devolvió `mfaRequired: true` + `preAuthToken`.
 *
 * Si el código es válido, el backend:
 *  - Actualiza DAT_LAST_MFA_VERIFICATION (reinicia la ventana de frecuencia)
 *  - Registra evento `mfa_success` (34) en TAB_SECURITY_LOGS
 *  - Emite un JWT completo de 8 horas con todos los claims de la sesión
 *
 * Si el código es inválido, registra evento `mfa_failed` (35) y retorna 401.
 * No hay límite de intentos por diseño (el código cambia cada 30s).
 *
 * Autenticación: PRE-AUTH TOKEN (NO el JWT completo).
 * El preAuthToken tiene claim `scope=mfa_only` y dura 2 minutos.
 *
 * @param {string} totpCode - Código TOTP de 6 dígitos
 * @param {string} preAuthToken - Token de pre-autenticación recibido de /login
 *
 * Ejemplo de llamada:
 * POST /api/Mfa/verify
 * Authorization: Bearer {preAuthToken}
 * { "totpCode": "654321" }
 *
 * Respuesta exitosa (200):
 * {
 *   "success": true,
 *   "message": "MFA verified successfully.",
 *   "token": "eyJhbGci...FULL_JWT_8H..."
 * }
 *
 * Posibles errores (401):
 * { "success": false, "result": 1, "message": "User not found or inactive" }
 * { "success": false, "result": 2, "message": "MFA not active for this user" }
 * { "success": false, "result": 4, "message": "Invalid TOTP code." }
 */
export const verifyMfa = async (totpCode, preAuthToken) => {
  try {
    const response = await axios.post(
      `${API_URL}/verify`,
      { totpCode },
      { headers: { Authorization: `Bearer ${preAuthToken}` } }
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      return error.response.data;
    }
    console.error("Error en verifyMfa:", error);
    throw error;
  }
};

/**
 * Validar un código de recuperación cuando el usuario no tiene acceso a su
 * app authenticator (celular perdido, sin batería, etc.). Cada código de
 * los 8 generados durante el enrollment solo puede usarse UNA VEZ.
 *
 * El backend:
 *  - Hashea el código con HMACSHA256 y lo busca en TAB_MFA_RECOVERY_CODES
 *  - Si lo encuentra sin usar, lo marca como BIT_USED = 1
 *  - Registra evento `mfa_recovery_used` (36)
 *  - Emite el JWT completo de 8 horas
 *  - Retorna `codesRemaining` (cuántos códigos quedan disponibles)
 *
 * Si `codesRemaining <= 2`, el frontend debe mostrar un banner persistente
 * recomendando al usuario re-generar el 2FA (desactivar + volver a activar).
 *
 * Autenticación: PRE-AUTH TOKEN (NO el JWT completo).
 *
 * @param {string} recoveryCode - Código en formato xxxx-xxxx-xxxx (case-insensitive)
 * @param {string} preAuthToken - Token de pre-autenticación recibido de /login
 *
 * Ejemplo de llamada:
 * POST /api/Mfa/recovery
 * Authorization: Bearer {preAuthToken}
 * { "recoveryCode": "a1b2-c3d4-e5f6" }
 *
 * Respuesta exitosa (200):
 * {
 *   "success": true,
 *   "message": "Recovery code accepted.",
 *   "codesRemaining": 7,
 *   "token": "eyJhbGci...FULL_JWT_8H..."
 * }
 *
 * Respuesta exitosa con advertencia (codesRemaining <= 2):
 * {
 *   "success": true,
 *   "message": "Recovery code accepted. WARNING: Only 2 recovery code(s) remaining. Please re-enroll 2FA.",
 *   "codesRemaining": 2,
 *   "token": "eyJhbGci..."
 * }
 *
 * Posibles errores (401):
 * { "success": false, "result": 1, "message": "User not found or inactive" }
 * { "success": false, "result": 2, "message": "MFA not active for this user" }
 * { "success": false, "result": 3, "message": "Invalid or already used recovery code" }
 */
export const consumeMfaRecoveryCode = async (recoveryCode, preAuthToken) => {
  try {
    const response = await axios.post(
      `${API_URL}/recovery`,
      { recoveryCode },
      { headers: { Authorization: `Bearer ${preAuthToken}` } }
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      return error.response.data;
    }
    console.error("Error en consumeMfaRecoveryCode:", error);
    throw error;
  }
};

/**
 * Desactivar 2FA para un usuario. Puede usarse en dos escenarios:
 *
 *  1. Auto-desactivación: el propio usuario desactiva su 2FA desde el perfil.
 *     → `targetEmployeeId` debe coincidir con el employeeId del JWT.
 *
 *  2. Desactivación admin: un administrador desactiva el 2FA de otro usuario
 *     (p.ej. el usuario perdió el celular y todos sus códigos de recuperación).
 *     → `targetEmployeeId` distinto al del JWT.
 *
 * El actorEmployeeId se lee del claim NameIdentifier del JWT (no se envía en
 * el body). El backend registra el evento `mfa_disabled` (33) y elimina los
 * códigos de recuperación asociados.
 *
 * Efectos:
 *  - BIT_MFA_ENABLED = 0
 *  - VAR_MFA_SECRET = NULL
 *  - DAT_MFA_ENROLLED = NULL
 *  - Todos los códigos de recuperación del usuario eliminados
 *
 * Autenticación: JWT completo.
 *
 * @param {number} targetEmployeeId - ID del empleado al que se le desactiva el 2FA
 *
 * Ejemplo de llamada:
 * POST /api/Mfa/disable
 * { "targetEmployeeId": 89 }
 *
 * Respuesta exitosa (200):
 * { "success": true, "message": "Success" }
 *
 * Posibles errores (400):
 * { "success": false, "result": 1, "message": "User not found or inactive" }
 * { "success": false, "result": 2, "message": "MFA is not currently active for this user" }
 */
export const disableMfa = async (targetEmployeeId) => {
  try {
    const response = await axios.post(
      `${API_URL}/disable`,
      { targetEmployeeId },
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      return error.response.data;
    }
    console.error("Error en disableMfa:", error);
    throw error;
  }
};

/**
 * Configurar la frecuencia (en días) con la que un usuario debe re-verificar
 * su 2FA durante el login. Solo para uso administrativo.
 *
 * La frecuencia NO significa que el 2FA se pida cada N días exactos desde el
 * mismo día, sino cuando han pasado N días o más desde la última verificación
 * exitosa (evaluado en SP_CHECK_MFA_REQUIRED durante cada login).
 *
 * Valores recomendados por rol:
 *  - Admin / CEO          → 1 día  (cada login)
 *  - Care Management      → 7 días (semanal)
 *  - PCP (médicos)        → 14 días (quincenal, default)
 *  - Médicos mayores      → 30 días (mensual)
 *
 * Autenticación: JWT completo (la UI debe limitar a rol admin).
 *
 * @param {number} targetEmployeeId - ID del empleado cuya frecuencia se configura
 * @param {number} frequencyDays - Número de días entre verificaciones (1-365)
 *
 * Ejemplo de llamada:
 * PUT /api/Mfa/frequency
 * { "targetEmployeeId": 89, "frequencyDays": 30 }
 *
 * Respuesta exitosa (200):
 * { "success": true, "message": "Success" }
 *
 * Posibles errores (400):
 * { "success": false, "result": 1, "message": "User not found or inactive" }
 * { "success": false, "result": 2, "message": "MFA frequency must be between 1 and 365 days" }
 * { "success": false, "result": 3, "message": "MFA frequency is already set to that value" }
 */
export const setMfaFrequency = async (targetEmployeeId, frequencyDays) => {
  try {
    const response = await axios.put(
      `${API_URL}/frequency`,
      { targetEmployeeId, frequencyDays },
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      return error.response.data;
    }
    console.error("Error en setMfaFrequency:", error);
    throw error;
  }
};
